import { create } from 'zustand';
import axiosInstance from '../utils/axiosInstance';
import useBookingStore from './bookingStore';
import { message } from 'antd';

const useAirbnbCalendarStore = create((set, get) => ({
  calendarData: null,
  loading: false,
  error: null,
  importedEvents: [],
  processingStatus: null, // Track the current processing status for debugging
  
  fetchCalendarData: async (url) => {
    set({ loading: true, error: null });
    try {
      const response = await axiosInstance.post('/platforms/airbnb/calendar/', { url });
      set({ calendarData: response.data, loading: false });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch calendar data';
      set({ error: errorMessage, loading: false });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  },
  
  // New method for importing individual bookings
  importBookingFromAirbnb: async (event, platformId, propertyId) => {
    set({ loading: true, error: null });
    
    const { createBooking, updateBooking, fetchBookings } = useBookingStore.getState();
    
    try {
      // First check if this booking already exists by external_id
      const { bookings } = useBookingStore.getState();
      const existingBooking = bookings.find(booking => booking.external_id === event.uid);
      
      // Use the event summary directly as the status
      // This uses the exact text from Airbnb like "Reserved" instead of hardcoded values
      const bookingData = {
        platform: platformId,
        check_in: new Date(event.start).toISOString().split('T')[0],
        check_out: new Date(event.end).toISOString().split('T')[0],
        status: event.summary || "Reserved", // Use summary directly as status
        property_id: propertyId,
        external_id: event.uid,
        notes: event.description || '',
        customer_name: ''
      };
      
      console.log(`Processing event: ${event.uid}`);
      console.log('Booking data:', bookingData);
      
      let result;
      
      if (existingBooking) {
        // Update existing booking
        console.log(`Updating existing booking for: ${event.uid}`);
        result = await updateBooking(existingBooking.id, bookingData);
      } else {
        // Create new booking
        console.log(`Creating new booking for: ${event.uid}`);
        result = await createBooking(bookingData);
      }
      
      if (result.success) {
        // Add to imported events so we can track it in the UI
        set(state => ({ 
          importedEvents: [...state.importedEvents, event.uid],
          loading: false 
        }));
        
        message.success(`Successfully imported booking from Airbnb`);
        return { success: true, data: result.data };
      } else {
        set({ loading: false, error: result.error });
        // Error message is already shown by the booking store
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error importing booking from Airbnb:', error);
      const errorMessage = typeof error === 'string' ? error : 
                          error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to import booking';
      
      set({ loading: false, error: errorMessage });
      message.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  },
  
  // Keep for backward compatibility but mark as deprecated
  importBookingsFromAirbnb: async (events, platformId, propertyId) => {
    console.warn('importBookingsFromAirbnb is deprecated, use importBookingFromAirbnb instead');
    message.warning('Batch import is no longer supported. Please import bookings individually.');
    return { success: [], failed: events, error: 'Batch import is deprecated' };
  },
  
  clearCalendarData: () => {
    set({ calendarData: null, error: null });
  },
  
  resetImportedEvents: () => {
    set({ importedEvents: [] });
  }
}));

export default useAirbnbCalendarStore;